import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, PaperPlaneTilt, Robot, BookOpen, ListChecks, Sparkle, Microphone,
  Camera, X, Image, Upload, Lock, Crown
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import GamePill from '../components/GamePill';
import { toast } from 'sonner';
import { useQuota, useEntitlement, useSmartNudges } from '../hooks/useEntitlement';
import { UpgradeBanner, QuotaIndicator, SmartNudgeStack } from '../components/UpgradePrompts';
import { useAiConsent } from '../components/AiConsentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GAMES = ['General', 'Catan', 'Chess', 'D&D', 'Magic: The Gathering', 'Pokemon', 'Yu-Gi-Oh!', 'Warhammer', 'Other'];

export default function AIAssistantPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [selectedGame, setSelectedGame] = useState(searchParams.get('game') || 'General');
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const chatEndRef = useRef(null);
  const aiQuota = useQuota('ai.rule_questions');
  const { nudges, dismiss: dismissNudge } = useSmartNudges();
  const cvEntitlement = useEntitlement('analytics.advanced');
  const { ensureConsent, AiConsentModalElement } = useAiConsent();

  // CV Board Detection state
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanImage, setScanImage] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cvSessionId, setCvSessionId] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    fetchChatHistory();
  }, [selectedGame]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchChatHistory = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ai/chat-history`, {
        params: { game_name: selectedGame, limit: 10 },
        withCredentials: true
      });
      setChatHistory(data);
    } catch (err) { /* silent */ }
  };

  const askQuestion = async () => {
    if (!question.trim() || loading) return;
    // Apple Guideline 5.1.1 — explicit consent for sending data to a third-party AI service.
    if (!(await ensureConsent())) return;
    // Check quota before asking
    const usage = await aiQuota.trackUsage();
    if (!usage.allowed) {
      toast.error('AI question limit reached. Upgrade your plan for more.');
      return;
    }
    const q = question.trim();
    setQuestion('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/ai/ask`, {
        game_name: selectedGame,
        question_text: q
      }, { withCredentials: true });

      setMessages(prev => [...prev, { role: 'ai', text: data.answer }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I encountered an error. Please try again.' }]);
      toast.error('AI request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Image too large (max 20MB)');
      return;
    }
    setScanImage(file);
    setScanPreview(URL.createObjectURL(file));
    setShowScanModal(true);
  };

  const analyzeBoard = async (customPrompt) => {
    if (!scanImage) return;
    // Apple Guideline 5.1.1 — consent before uploading the photo to OpenAI for CV analysis.
    if (!(await ensureConsent())) { setShowScanModal(false); return; }
    setScanning(true);
    setShowScanModal(false);

    const prompt = customPrompt || `Analyze this board game image. The game might be ${selectedGame}. Identify the game, analyze the board state, check if setup is correct, and provide strategic observations.`;
    setMessages(prev => [...prev, { role: 'user', text: `[Board Scan] ${prompt}`, isImage: true, preview: scanPreview }]);

    try {
      const reader = new FileReader();
      const b64 = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(scanImage);
      });

      const { data } = await axios.post(`${API_URL}/api/cv-detect/analyze`, {
        image: b64,
        prompt: prompt,
      }, { withCredentials: true });

      setCvSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'ai', text: data.analysis, isCvResult: true }]);
    } catch (err) {
      const errMsg = err.response?.status === 403
        ? 'CV Board Detection is a Pro feature. Upgrade to unlock!'
        : 'Failed to analyze image. Please try again.';
      setMessages(prev => [...prev, { role: 'ai', text: errMsg }]);
      if (err.response?.status === 403) toast.error('Pro feature — upgrade to use Board Scan');
      else toast.error('Analysis failed');
    } finally {
      setScanning(false);
      setScanImage(null);
    }
  };

  const askCvFollowup = async () => {
    if (!question.trim() || loading || !cvSessionId) return;
    if (!(await ensureConsent())) return;
    const q = question.trim();
    setQuestion('');
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/cv-detect/followup`, {
        session_id: cvSessionId,
        question: q,
      }, { withCredentials: true });
      setMessages(prev => [...prev, { role: 'ai', text: data.analysis, isCvResult: true }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, follow-up failed.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container" data-testid="ai-assistant-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <Robot size={24} weight="duotone" className="text-[#00F0FF]" />
              CyBoard AI
            </h1>
            <p className="text-[#71717A] text-xs">Ask rules, get tips, learn games</p>
          </div>
          <div className="ml-auto">
            <QuotaIndicator used={aiQuota.used} limit={aiQuota.limit} label="questions" />
          </div>
        </div>

        <UpgradeBanner used={aiQuota.used} limit={aiQuota.limit} featureName="AI Questions" plan="Plus" />

        {/* Smart Nudge for AI heavy users */}
        <SmartNudgeStack nudges={nudges.filter(n => n.feature_highlight?.includes('ai'))} maxShow={1} onDismiss={dismissNudge} />

        {/* Game Selector */}
        <div className="horizontal-scroll mb-3">
          {GAMES.map(g => (
            <GamePill key={g} game={g} selected={selectedGame === g} onClick={() => { setSelectedGame(g); setMessages([]); }} />
          ))}
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 mb-3">
          <button onClick={() => navigate(`/rules?game=${selectedGame}`)} className="flex-1 p-2.5 rounded-xl bg-[#181824] border border-white/5 text-sm text-white flex items-center gap-2 hover:border-[#9D4CDD]/30 transition-all" data-testid="rules-link">
            <BookOpen size={16} weight="duotone" className="text-[#FACC15]" />
            Rules
          </button>
          <button onClick={() => navigate(`/tutorial?game=${selectedGame}`)} className="flex-1 p-2.5 rounded-xl bg-[#181824] border border-white/5 text-sm text-white flex items-center gap-2 hover:border-[#9D4CDD]/30 transition-all" data-testid="tutorial-link">
            <ListChecks size={16} weight="duotone" className="text-[#39FF14]" />
            Tutorial
          </button>
          <button
            onClick={() => {
              if (!cvEntitlement.allowed) { toast.error('Board Scan is a Plus/Pro feature'); navigate('/pricing'); return; }
              fileInputRef.current?.click();
            }}
            className="flex-1 p-2.5 rounded-xl border text-sm text-white flex items-center gap-2 transition-all relative overflow-hidden"
            style={{
              backgroundColor: cvEntitlement.allowed ? '#181824' : '#181824',
              borderColor: cvEntitlement.allowed ? 'rgba(0,240,255,0.15)' : 'rgba(255,255,255,0.03)',
            }}
            data-testid="scan-board-btn"
          >
            <Camera size={16} weight="duotone" className="text-[#00F0FF]" />
            Scan
            {!cvEntitlement.allowed && <Crown size={10} weight="fill" className="text-[#F59E0B] absolute top-1 right-1" />}
          </button>
          <button onClick={() => navigate(`/voice?game=${selectedGame}`)} className="flex-1 p-2.5 rounded-xl bg-[#181824] border border-white/5 text-sm text-white flex items-center gap-2 hover:border-[#EF4444]/30 transition-all" data-testid="voice-link">
            <Microphone size={16} weight="duotone" className="text-[#EF4444]" />
            Voice
          </button>
        </div>

        {/* Hidden file inputs for image selection */}
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} data-testid="scan-file-input" />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleImageSelect} />
      </header>

      {/* Chat Area */}
      <div className="flex-1 px-6 pb-40 overflow-y-auto space-y-3" style={{ maxHeight: 'calc(100vh - 340px)' }}>
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Robot size={48} weight="duotone" className="text-[#9D4CDD] mx-auto mb-3 opacity-50" />
            <p className="text-[#71717A] text-sm">
              {selectedGame === 'General'
                ? <>Ask me anything about <span className="text-[#00F0FF]">tabletop gaming</span></>
                : <>Ask me anything about <span className="text-[#00F0FF]">{selectedGame}</span></>}
            </p>
            <div className="mt-4 space-y-2">
              {(selectedGame === 'General'
                ? ['Recommend a 2-player strategy game', 'How do I run my first D&D session?', 'Compare Wingspan vs Ark Nova']
                : ['How do the core rules work?', 'What are common strategies?', 'Tips for beginners?']
              ).map(sq => (
                <button key={sq} onClick={() => { setQuestion(sq); }} className="block mx-auto px-4 py-2 rounded-lg bg-[#181824] border border-white/5 text-[#A1A1AA] text-xs hover:text-white hover:border-[#9D4CDD]/30 transition-all">
                  "{sq}"
                </button>
              ))}
            </div>
            {cvEntitlement.allowed && (
              <div className="mt-6 p-4 rounded-xl bg-[#00F0FF]/[0.03] border border-[#00F0FF]/10">
                <Camera size={24} weight="duotone" className="text-[#00F0FF] mx-auto mb-2" />
                <p className="text-[#00F0FF] text-xs font-medium">Board Scan Available</p>
                <p className="text-[#555] text-[10px] mt-1">Tap "Scan" above to photograph a board game for AI analysis</p>
              </div>
            )}
          </div>
        )}

        <AnimatePresence>
          {messages.map((msg, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white rounded-br-md'
                  : msg.isCvResult
                    ? 'bg-[#0c1a24] border border-[#00F0FF]/15 text-[#E4E4E7] rounded-bl-md'
                    : 'bg-[#181824] border border-white/5 text-[#E4E4E7] rounded-bl-md'
              }`}>
                {msg.role === 'ai' && msg.isCvResult && (
                  <span className="text-[10px] text-[#00F0FF] font-medium block mb-1 flex items-center gap-1">
                    <Camera size={10} /> Board Analysis
                  </span>
                )}
                {msg.role === 'ai' && !msg.isCvResult && <span className="text-[10px] text-[#00F0FF] font-medium block mb-1">AI Response</span>}
                {msg.isImage && msg.preview && (
                  <div className="mb-2 rounded-lg overflow-hidden border border-white/10" style={{ maxWidth: 200 }}>
                    <img src={msg.preview} alt="Board scan" className="w-full h-auto" />
                  </div>
                )}
                <div className="whitespace-pre-wrap">{msg.text}</div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {(loading || scanning) && (
          <div className="flex justify-start">
            <div className="p-3 rounded-2xl bg-[#181824] border border-white/5 rounded-bl-md">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#9D4CDD] animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 rounded-full bg-[#9D4CDD] animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 rounded-full bg-[#9D4CDD] animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                {scanning && <span className="text-[10px] text-[#00F0FF]">Analyzing board...</span>}
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input Bar */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-[#0A0A12]/95 backdrop-blur-lg border-t border-white/5 max-w-[480px] mx-auto">
        {cvSessionId && (
          <div className="flex items-center gap-2 mb-2 px-1">
            <Camera size={12} className="text-[#00F0FF]" />
            <span className="text-[10px] text-[#00F0FF]">Board scan active — ask follow-up questions</span>
            <button onClick={() => setCvSessionId(null)} className="ml-auto text-[10px] text-[#555] hover:text-white" data-testid="clear-cv-session">Clear</button>
          </div>
        )}
        <div className="flex gap-2">
          <input
            type="text"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (cvSessionId ? askCvFollowup() : askQuestion())}
            placeholder={cvSessionId ? 'Ask about the board...' : (selectedGame === 'General' ? 'Ask anything about tabletop gaming...' : `Ask about ${selectedGame}...`)}
            className="input-field flex-1"
            data-testid="ai-question-input"
          />
          <button onClick={cvSessionId ? askCvFollowup : askQuestion} disabled={loading || scanning || !question.trim()} className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center disabled:opacity-50 transition-all" data-testid="ai-send-button">
            <PaperPlaneTilt size={20} weight="fill" className="text-white" />
          </button>
        </div>
      </div>

      {/* Scan Modal */}
      <AnimatePresence>
        {showScanModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowScanModal(false)}
          >
            <motion.div
              initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-[480px] bg-[#0e0e16] border-t border-white/10 rounded-t-2xl p-5"
              data-testid="scan-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-base flex items-center gap-2">
                  <Camera size={20} weight="duotone" className="text-[#00F0FF]" /> Board Scan
                </h3>
                <button onClick={() => { setShowScanModal(false); setScanImage(null); setScanPreview(null); }} className="text-[#555] hover:text-white">
                  <X size={18} />
                </button>
              </div>

              {scanPreview && (
                <div className="rounded-xl overflow-hidden border border-white/10 mb-4" style={{ maxHeight: 250 }}>
                  <img src={scanPreview} alt="Board preview" className="w-full h-auto object-contain" style={{ maxHeight: 250 }} />
                </div>
              )}

              <p className="text-[#777] text-xs mb-4">
                The AI will identify the game, analyze board state, verify setup, and provide strategic insights.
              </p>

              <div className="space-y-2">
                <button
                  onClick={() => analyzeBoard()}
                  disabled={scanning}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-[#00F0FF]/20 to-[#9D4CDD]/20 border border-[#00F0FF]/20 text-white font-semibold text-sm hover:border-[#00F0FF]/40 transition-all flex items-center justify-center gap-2"
                  data-testid="analyze-board-btn"
                >
                  <Sparkle size={16} weight="fill" className="text-[#00F0FF]" />
                  {scanning ? 'Analyzing...' : 'Analyze Board'}
                </button>
                <button
                  onClick={() => analyzeBoard(`Check if this ${selectedGame} board is set up correctly according to the official rules. Point out any issues.`)}
                  disabled={scanning}
                  className="w-full py-3 rounded-xl bg-[#181824] border border-white/5 text-white text-sm hover:border-white/10 transition-all flex items-center justify-center gap-2"
                  data-testid="verify-setup-btn"
                >
                  <ListChecks size={16} weight="duotone" className="text-[#39FF14]" />
                  Verify Setup
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
      {AiConsentModalElement}
    </div>
  );
}
